import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { DownloadOutlined } from '@mui/icons-material';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import Loading from 'components/Loading';
import MessageRenderer from 'components/MessageRenderer';
import { lookupTranscript } from 'utils/methods/assignmentTranscripts';

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownToggle = styled.div`
  cursor: pointer;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 15px;
  margin-top: -5px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 9999;
  min-width: 150px;
`;

const DropdownItem = styled.div`
  padding: 8px 16px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TranscriptBlock = styled.div`
  margin-top: 6px;
  font-size: 14px;
  line-height: 1.4;
  color: #000;
  width: 100%;
`;

const TranscriptWithAudio = ({
  children,
  audioUrl,
  paragraph,
  isInteractionAudio = false,
  transcripts,
}) => {
  const { downloadTranscipt, districtPermissionConfig, downloadAudio } = usePermissionForUser();
  const { audio_transcript_download = false, audio_download = false } = districtPermissionConfig;
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const inlineTranscript =
    transcripts && audioUrl ? lookupTranscript(transcripts, audioUrl) : null;

  const handleDownloadTranscript = async () => {
    setIsLoading(true);
    setIsDropdownOpen(false);
    await downloadTranscipt({ audioUrl, paragraph });
    setIsLoading(false);
  };

  const handleDownloadAudio = async () => {
    setIsLoading(true);
    setIsDropdownOpen(false);
    await downloadAudio({ audioUrl });
    setIsLoading(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      style={{
        width: isInteractionAudio ? '100%' : '96%',
        justifyContent: 'center',
      }}
      className="flex flex-col gap-5px"
    >
      <div
        style={{ width: '100%', justifyContent: 'center' }}
        className="flex items-center gap-5px"
      >
        {children}
        {!isInteractionAudio && (audio_transcript_download || audio_download) && (
          <div className="flex items-center justify-end">
            <DropdownContainer ref={dropdownRef}>
              <DropdownToggle
                className="flex items-center gap-5px"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                {isLoading && <Loading size={16} />}
                <DownloadOutlined style={{ fontSize: 22, cursor: 'pointer' }} />
              </DropdownToggle>

              {isDropdownOpen && (
                <DropdownMenu>
                  {audio_download && (
                    <DropdownItem onClick={handleDownloadAudio}>Download Audio</DropdownItem>
                  )}
                  {audio_transcript_download && (
                    <DropdownItem onClick={handleDownloadTranscript}>
                      Download Transcript
                    </DropdownItem>
                  )}
                </DropdownMenu>
              )}
            </DropdownContainer>
          </div>
        )}
      </div>
      {inlineTranscript && (
        <TranscriptBlock>
          <MessageRenderer data={inlineTranscript} />
        </TranscriptBlock>
      )}
    </div>
  );
};

export default TranscriptWithAudio;
